import type { AppUser } from '@lighthouse/core'
import { AppUserStatus, DataSourceType } from '@lighthouse/core'
import type { DeptOption, RoleOption, UserOption } from '@lighthouse/shared'
import {
    DEPARTMENT_DATASOURCE,
    getFieldOptions,
    getTableIcon,
    isStringArray,
    ROLE_DATASOURCE,
    useAtomData,
    useEqualArrayValue,
    USER_DATASOURCE
} from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useMemo } from 'react'

import { currentEnvIdAtom } from '@/atoms/application/state'
import {
    currentDataSourceIdAtom,
    dataSourceAtomFamily,
    // dataSourceEnvIdAtom,
    dataSourcePoolAtom,
    recordAtomFamily,
    recordListAtomFamily
} from '@/atoms/dataSource/state'

import { useCurrentAppId } from './useApplication'

export const useDataSourceEnvId = () => {
    // return useAtomValue(dataSourceEnvIdAtom)
    return useAtomValue(currentEnvIdAtom)
}

export const useDataSource = (appId: string, envId: string, dsId: string) => {
    return useAtomValue(dataSourceAtomFamily({ appId, envId, dsId }))
}

export const useRecord = (appId: string, envId: string, dsId: string, recordId: string) => {
    return useAtomValue(recordAtomFamily({ appId, envId, dsId, recordId }))
}

export const useCurrentDataSourceId = () => {
    return useAtomValue(currentDataSourceIdAtom)
}

export const useDataSourceList = (appId: string, envId: string) => {
    return useAtomData(
        useMemo(
            () =>
                selectAtom(dataSourcePoolAtom, draft =>
                    draft.filter(dataSource => dataSource.appId === appId && dataSource.envId === envId)
                ),
            [appId, envId]
        )
    )
}

export const useNotAggregateDataSourceList = (appId: string, envId: string) => {
    return useAtomData(
        useMemo(
            () =>
                selectAtom(dataSourcePoolAtom, draft =>
                    draft.filter(
                        dataSource =>
                            dataSource.appId === appId &&
                            dataSource.envId === envId &&
                            dataSource.type !== DataSourceType.aggregateDataSource
                    )
                ),
            [appId, envId]
        )
    )
}

export const useDataSourceOptions = () => {
    const appId = useCurrentAppId()
    const envId = useDataSourceEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    return useMemo(() => {
        return dataSourceList.map(item => {
            const tableIcon = getTableIcon(item)
            return {
                label: item.name,
                value: item.id,
                icon: tableIcon,
                groupValue: item.appId,
                groupLabel: item.appName
            }
        })
    }, [dataSourceList])
}

export const useDsListAndFieldOptions = (appId: string, envId: string, dsId: string) => {
    const dataSourceList = useDataSourceList(appId, envId)
    const dataSource = useDataSource(appId, envId, dsId)
    const { schema, viewOptions: { tableProps = [] } = {} } = dataSource || {}

    const fieldOptions = useMemo(() => getFieldOptions(schema, tableProps), [schema, tableProps])

    const dataSourceOptions = useDataSourceOptions()

    return { dataSourceOptions, fieldOptions, dataSource, dataSourceList }
}

/**
 * @description 获取数据源用户列表
 * @export
 * @return {*}
 */
export function useDataSourceUsers() {
    const appId = useCurrentAppId()
    const records = useAtomData(recordListAtomFamily({ appId, dsId: USER_DATASOURCE }))

    const value = records
        .filter(record => {
            const status = record.content['STATUS']?.value
            return !(Array.isArray(status) && status?.[0] === AppUserStatus.REVIEWED)
        })
        .map(record => {
            const data = record.content
            return {
                id: String(data['ID']?.value || ''),
                name: String(data['USERNAME']?.value || '未命名用户'),
                avatar: isStringArray(data['AVATAR']?.value) ? data['AVATAR']?.value?.[0] ?? '' : '',
                status: isStringArray(data['STATUS']?.value) ? data['STATUS']?.value?.[0] : undefined
            } satisfies UserOption
        })

    return useEqualArrayValue(value)
}

/** Full records remain available to builder-only permission simulation. */
export function useDataSourceUserDetails() {
    const appId = useCurrentAppId()
    const records = useAtomData(recordListAtomFamily({ appId, dsId: USER_DATASOURCE }))
    const value = records.map(record => {
        const data = record.content
        return {
            uniqueUserId: record.id,
            departmentIds: data['DEPARTMENT']?.value || [],
            createdTime: data['createdTime']?.value,
            email: data['EMAIL']?.value || '',
            mobile: data['MOBILE']?.value || '',
            roleId: data['ROLE']?.value || [],
            status: data['STATUS']?.value,
            userId: data['ID']?.value || '',
            avatar: isStringArray(data['AVATAR']?.value) ? data['AVATAR']?.value?.[0] ?? '' : '',
            username: data['USERNAME']?.value || '未命名用户',
            wechatOpenId: data['WECHAT_OPENID']?.value || '',
            wechatMiniProgramOpenId: data['WECHAT_MINI_PROGRAM_OPENID']?.value || ''
        } as AppUser
    })
    return useEqualArrayValue(value)
}

/**
 * @description 获取数据源角色列表
 * @export
 * @return {*}
 */
export function useDataSourceRoles() {
    const appId = useCurrentAppId()
    const records = useAtomValue(recordListAtomFamily({ appId, dsId: ROLE_DATASOURCE }))
    const value = records.map(record => {
        const data = record.content
        return {
            id: String(data['ID']?.value || ''),
            name: String(data['ROLE_NAME']?.value || '未命名角色'),
        } satisfies RoleOption
    })
    return useEqualArrayValue(value)
}
// RoleProtocols
/**
 * @description 获取数据源部门列表
 * @export
 * @return {*}
 */

export function useDataSourceDepartments() {
    const appId = useCurrentAppId()
    const records = useAtomValue(recordListAtomFamily({ appId, dsId: DEPARTMENT_DATASOURCE }))
    const value = records.map(record => {
        const data = record.content
        const option = {
            id: String(data['ID']?.value || ''),
            name: String(data['DEPARTMENT_NAME']?.value || '未命名部门'),
            parentId: isStringArray(data['PARENT_DEPARTMENT']?.value)
                ? data['PARENT_DEPARTMENT']?.value?.[0]
                : typeof data['PARENT_DEPARTMENT']?.value === 'string'
                  ? data['PARENT_DEPARTMENT'].value
                  : undefined
        } satisfies DeptOption
        return { ...option, recordId: record.id }
    })
    return useEqualArrayValue(value)
}
